<script setup>
/**
 * "When the chat comes back here, forget…" — the control that makes a jump-back work.
 *
 * ── WHY THIS EXISTS, AND WHY IT IS NOT OPTIONAL ──────────────────────────────────────
 * The engine's walk skips every step whose answer is already saved. That is what makes
 * filling ahead work, and it is what makes the commonest branch in a booking task a dead
 * end without this control: "that day is full — pick another" jumps back to the day
 * question, finds the day already answered, skips it, and lands straight back on the empty
 * list of times. Forgetting the day on the way in is the whole fix. The live Seamless graph
 * carries it on five of its sixteen steps, so a builder that can render that task and not
 * produce it is not finished.
 *
 * ── THE SEMANTICS, STATED ONCE AND CAREFULLY ─────────────────────────────────────────
 * It fires ON ARRIVAL at this step — never on the way out — and it never forgets an answer
 * the customer gave in the same message. Both halves matter and both were recently settled
 * in the engine, so the copy says exactly that and nothing more; a confident wrong sentence
 * about this would be worse than no sentence.
 *
 * A STEP MAY FORGET ITS OWN ANSWER, and that is not a mistake to validate away — it is
 * precisely how a re-ask is authored. The chooser offers it and says what it does.
 *
 * ── WHY IT IS A TICK LIST AND NOT A TEXT BOX ─────────────────────────────────────────
 * The options are exactly the keys `validate.ts` checks a `clears` entry against, so the
 * `unknown_clear` problem becomes unreachable from the builder rather than merely
 * reportable. Typing `start_hour` and meaning `start_time` forgets nothing, silently.
 *
 * ── AND WHY IT IS READ-ONLY ON SOME CARDS ────────────────────────────────────────────
 * Forgetting an answer on arrival at a step that ENDS the task has no observable effect, so
 * Hand over and Finish do not offer the control. But a definition may still carry one there
 * — hand-written, or from a newer sidecar — and a setting an owner cannot see is a behaviour
 * she cannot reason about, so it is shown as a sentence instead of hidden.
 */
import { computed } from 'vue';

import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

import { useWasloI18n } from '../i18n.js';
import { toIntlLocale } from '../helpers/intl.js';

const props = defineProps({
  /** The step's `clears` — save keys, in the spelling `saveKeyOf` produces. */
  modelValue: { type: Array, default: () => [] },
  /** `writtenKeys(definition)` — every answer this task collects. */
  candidates: { type: Array, default: () => [] },
  /** This step's own save key, so the re-ask case can be named as what it is. */
  ownKey: { type: String, default: '' },
  /** False on a card where forgetting an answer could have no effect. */
  editable: { type: Boolean, default: true },
});

const emit = defineEmits(['update:modelValue']);

const { t, locale } = useWasloI18n();

const key = name => `WASLO.TASKS.EDITOR.FLOW.CLEARS.${name}`;

const format = computed(
  () => new Intl.NumberFormat(toIntlLocale(locale.value))
);

const toggle = (candidate, on) => {
  const next = on
    ? [...props.modelValue, candidate.key]
    : props.modelValue.filter(entry => entry !== candidate.key);
  emit('update:modelValue', [...new Set(next)]);
};

/** Where an answer comes from, so the owner knows which question she is un-asking. */
const source = candidate => {
  if (candidate.onContact) return t(key('FROM_CONTACT'));
  return t(key('FROM_STEP'), {
    number: format.value.format(candidate.stepNumber),
    name:
      candidate.stepName.trim() || t('WASLO.TASKS.EDITOR.FLOW.CARD.UNNAMED'),
  });
};

/**
 * Anything ticked that this task no longer collects. It is kept and shown rather than
 * quietly dropped — the server reports it as a problem, and the card has to be able to
 * explain the tick that caused it.
 */
const orphans = computed(() =>
  props.modelValue.filter(
    entry => !props.candidates.some(candidate => candidate.key === entry)
  )
);

const chosenNames = computed(() =>
  props.modelValue
    .map(entry => {
      const candidate = props.candidates.find(row => row.key === entry);
      return candidate ? candidate.name : entry;
    })
    .join(t('WASLO.COMMON.LIST_SEPARATOR'))
);
</script>

<template>
  <div
    v-if="editable && candidates.length"
    class="flex flex-col gap-3"
    data-waslo-step-clears
  >
    <div class="flex flex-col gap-1">
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('HEADING')) }}
      </p>
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
        {{ t(key('DESCRIPTION')) }}
      </p>
    </div>

    <div class="flex flex-col gap-2">
      <label
        v-for="candidate in candidates"
        :key="candidate.key"
        class="flex items-start gap-3 px-3 py-2 rounded-lg cursor-pointer bg-n-alpha-1 hover:bg-n-alpha-2"
        :data-waslo-step-clear="candidate.key"
      >
        <Checkbox
          class="mt-1"
          :model-value="modelValue.includes(candidate.key)"
          @update:model-value="value => toggle(candidate, value)"
        />
        <span class="flex flex-col min-w-0 gap-0.5">
          <span class="text-sm break-words text-n-slate-12">
            <bdi>{{ candidate.name }}</bdi>
          </span>
          <span class="text-xs break-words text-n-slate-10">
            {{ source(candidate) }}
          </span>
          <!-- The re-ask, named. It is the one tick whose effect is not "go back further". -->
          <span
            v-if="candidate.key === ownKey"
            class="text-xs text-n-slate-10"
            data-waslo-step-clear-own
          >
            {{ t(key('OWN')) }}
          </span>
        </span>
      </label>
    </div>

    <p
      v-if="orphans.length"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-step-clears-orphans
    >
      {{
        t(key('ORPHANS'), {
          keys: orphans.join(t('WASLO.COMMON.LIST_SEPARATOR')),
        })
      }}
    </p>
  </div>

  <!-- Nothing to forget yet: said rather than shown as an empty tick list, which reads as a
       control that is broken. -->
  <p
    v-else-if="editable && modelValue.length === 0"
    class="mb-0 text-sm text-n-slate-11"
    data-waslo-step-clears-none
  >
    {{ t(key('NOTHING_TO_FORGET')) }}
  </p>

  <!-- A setting this card does not offer, kept and stated. -->
  <p
    v-else-if="modelValue.length"
    class="mb-0 text-sm text-n-slate-11"
    data-waslo-step-clears-readonly
  >
    {{ t(key('KEPT'), { keys: chosenNames }) }}
  </p>
</template>
